import { DateTime } from 'luxon';
import { z } from 'zod';

// Wire schemas match the internaltools `companies_directory` and
// `company_config_lookup` Lambda handlers verbatim — `_envelope()` wraps
// every response in `{ data, error }` and both endpoints POST-only. Request
// bodies stay camelCase (same split as the eligibility-match sibling on the
// same repo).

// ── envelope ───────────────────────────────────────────────────────────────

/**
 * Every internaltools handler returns a body of shape `{ data, error }`.
 * `data` is null on error responses; `error` is null on success. Both fields
 * are always present and required. Unknown keys are stripped rather than
 * rejected (ADR-135): the portal can add a field before every Galapagos deploy.
 */
function internaltoolsEnvelope<T extends z.ZodTypeAny>(data: T) {
  return z.object({
    data: data.nullable(),
    error: z.string().nullable(),
  });
}

// ── directory ──────────────────────────────────────────────────────────────

/**
 * Each directory entry keys the company by the string `ref` — the Lambda
 * does `str(company_id)` before returning, so the wire type is a string
 * even though the DB column is an int. Nothing else on the wire
 * distinguishes decoy (`usage='test'`) rows from real sponsors. Unknown keys
 * are stripped, so only `ref` and `name` ever leave this schema (ADR-135).
 */
export const companyDirectoryEntryWireSchema = z.object({
  ref: z.string(),
  name: z.string(),
});

export const companiesDirectoryDataSchema = z.object({
  companies: z.array(companyDirectoryEntryWireSchema),
  // Present only on a parent-scoped reply: the parent id the portal filtered
  // by, echoed so the caller can tell a scoped list from an unscoped one.
  parent_ref: z.string().optional(),
});

export const companiesDirectoryResponseSchema = internaltoolsEnvelope(companiesDirectoryDataSchema);

export type CompanyDirectoryEntryWire = z.infer<typeof companyDirectoryEntryWireSchema>;
export type CompaniesDirectoryResponse = z.infer<typeof companiesDirectoryResponseSchema>;

// ── config lookup ──────────────────────────────────────────────────────────

/**
 * `company_id` is a JSON integer (the Lambda passes `long_param` and the
 * response echoes the int back); `configs` is deliberately open so a new
 * portal key does not break the schema. The collapse rule
 * (see collapseProgramKeys) picks only boolean-`true`
 * `program_<internaltools-key>_enabled` entries and translates each into a
 * Galapagos program key via INTERNALTOOLS_PROGRAM_KEY_MAP.
 */
// Non-strict (ADR-135): internaltools may add fields before every Galapagos deploy;
// strict parsing would fail closed on the eligibility company-config path.
export const companyConfigLookupDataSchema = z.object({
  company_id: z.number().int(),
  // Staff sponsor label from internaltools (parent-sub or standalone). Optional
  // so parsers accept deployments that predate the field.
  display_name: z.string().nullable().optional(),
  configs: z.record(z.string(), z.unknown()),
});

export const companyConfigLookupResponseSchema = internaltoolsEnvelope(
  companyConfigLookupDataSchema,
);

export type CompanyConfigLookupData = z.infer<typeof companyConfigLookupDataSchema>;
export type CompanyConfigLookupResponse = z.infer<typeof companyConfigLookupResponseSchema>;

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar day in `YYYY-MM-DD` form: the shape AND the date (no 2026-02-30). */
const calendarDaySchema = z
  .string()
  .refine((value) => ISO_DAY.test(value) && DateTime.fromISO(value, { zone: 'utc' }).isValid);

// Canonical, camelCase, server-only. Produced by the `internaltools` provider
// after the ADR-015 collapse: `programKeys` holds Galapagos program-catalog
// keys (a subset of `tobacco` / `alcohol` / `opioid` / `cannabis` /
// `stimulants` — the `program_definitions.key` vocabulary from
// scripts/db-tooling/seed-member-fixtures.ts). The pass-through `configs`
// snapshot is preserved so a future server consumer can read non-program
// fields without another round-trip; mobile MUST NOT read
// substance-specific keys from it directly.
export const companyConfigSchema = z.object({
  companyId: z.number().int(),
  programKeys: z.array(z.string()),
  /** Sponsor display label when internaltools supplies one; null means show the ref. */
  displayName: z.string().nullable().optional(),
  configs: z.record(z.string(), z.unknown()),
  /**
   * Whether this client buys NRT fulfilment — 1.0's `isOptedInToNrt`, migrated
   * to company config as `nrt_enabled` (Client Portal CLI-98 Slice 1.5).
   *
   * TYPED, not read out of `configs` as `unknown`, so a key or type change
   * upstream is a parse event we can see rather than a silent `undefined` that
   * reads as "no NRT" for every client at once.
   *
   * OPTIONAL and CATCHING, per ADR-135 Rule 2: a response that omits it, or
   * sends something that is not a boolean, degrades to `undefined` here instead
   * of failing the whole lookup — the other consumers of this schema
   * (eligibility, rewards, check-ins) must not start erroring because an NRT
   * field moved. `undefined` is NOT "false": it means UNKNOWN, and the caller
   * decides what unknown costs. See `nrt-entitlement.ts`.
   */
  nrt_enabled: z.boolean().optional().catch(undefined),
  /**
   * The ordered list of Apero payer slugs the client's members are checked
   * against (1.0's `b2b_companies.payers`), carried as `apero_payers` inside
   * `configs` once the client portal exposes it. Same posture as
   * `nrt_enabled`: typed, optional and catching, so a missing or malformed
   * value reads as UNKNOWN here and never fails another reader. UNKNOWN is
   * treated as "no payers" by the Apero consumer, which then runs no check.
   */
  apero_payers: z.array(z.string().min(1)).optional().catch(undefined),
  /**
   * Whether the client is a real sponsor or a test one (1.0's
   * `b2b_companies.usage`), carried as `usage` inside `configs`. Optional and
   * catching for the same reason; UNKNOWN is treated as "not a test company".
   */
  usage: z.enum(['test', 'live']).optional().catch(undefined),
  /**
   * Whether the client applies a tobacco surcharge, carried in `configs` as the
   * string `"Yes"` or `"No"`. Normalised to `'yes'` / `'no'` regardless of case
   * or surrounding spaces. Optional and catching like `nrt_enabled`: anything
   * else, a boolean included, reads as UNKNOWN, never as either answer.
   */
  tobacco_surcharge: z
    .string()
    .transform((value) => value.trim().toLowerCase())
    .pipe(z.enum(['yes', 'no']))
    .optional()
    .catch(undefined),
  /** The client's surcharge period, `YYYY-MM-DD` calendar days; anything else reads as unknown. */
  tobacco_surcharge_period_start: calendarDaySchema.optional().catch(undefined),
  tobacco_surcharge_period_end: calendarDaySchema.optional().catch(undefined),
});

export type CompanyConfig = z.infer<typeof companyConfigSchema>;

// ── program-key vocabulary translation ─────────────────────────────────────

/**
 * Internaltools' portal catalog keys programs by clinical substance name
 * (see src/portal/configCatalog.ts) — `cigarettes`, `eCigarettes`,
 * `chewingTobacco`, `alcohol`, `opioids`, `cannabis`, `stimulants` — while
 * Galapagos' `program_definitions.key` catalog uses a smaller
 * program-domain vocabulary — `tobacco`, `alcohol`, `opioid`, `cannabis`,
 * `stimulants` — where all three nicotine variants collapse into one
 * `tobacco` program (see scripts/db-tooling/seed-member-fixtures.ts).
 *
 * This map is the boundary. Keys not present here are dropped from
 * `programKeys` — the internaltools portal supports a few advanced-tier
 * "Learn" variants (`cigarettesLearn`, `eCigarettesLearn`) that Galapagos
 * has no program for, and any future addition should land explicitly here
 * rather than pass through silently.
 */
export const INTERNALTOOLS_PROGRAM_KEY_MAP: Readonly<Record<string, string>> = {
  cigarettes: 'tobacco',
  eCigarettes: 'tobacco',
  chewingTobacco: 'tobacco',
  alcohol: 'alcohol',
  opioids: 'opioid',
  cannabis: 'cannabis',
  stimulants: 'stimulants',
} as const;

// Extracted so both the live provider and its unit tests share one
// definition of "which keys look like a program-enabled flag". Note the
// `.+?` group is case-sensitive on purpose — internaltools' key set uses
// camelCase segments (`eCigarettes`, `chewingTobacco`) and lowercasing
// would break the map lookup.
export const PROGRAM_ENABLED_KEY_PATTERN = /^program_(.+?)_enabled$/;

/**
 * Pure collapse of the raw internaltools `configs` map into a sorted-deduped
 * Galapagos `programKeys` array. Boolean-strict (string `"true"` and
 * numeric `1` are ignored — matching the internaltools boolean column).
 * Multiple internaltools keys can map to the same Galapagos program (three
 * nicotine variants → `tobacco`), so the `Set` dedupes the mapped result,
 * not the raw key.
 */
export function collapseProgramKeys(configs: Readonly<Record<string, unknown>>): string[] {
  const keys = new Set<string>();
  for (const [rawKey, rawValue] of Object.entries(configs)) {
    if (rawValue !== true) continue;
    const match = PROGRAM_ENABLED_KEY_PATTERN.exec(rawKey);
    if (!match) continue;
    const internaltoolsKey = match[1];
    const galapagosKey = INTERNALTOOLS_PROGRAM_KEY_MAP[internaltoolsKey];
    if (galapagosKey) keys.add(galapagosKey);
  }
  return Array.from(keys).sort();
}
